'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SafeStorage } from '../utils/safeStorage';

export type YapSortOption = 'trending' | 'most-engagement' | 'newest';

// Valid sort values (used to sanitize a stale/removed value read from storage,
// e.g. a previously-saved 'oldest' after that option was removed).
const VALID_SORTS: YapSortOption[] = ['trending', 'most-engagement', 'newest'];

// Retired options → their closest surviving equivalent. 'most-likes'/'most-shouts'
// were both single-signal engagement sorts, so they fold into 'most-engagement';
// anything else unrecognised falls back to the default below.
const SORT_MIGRATIONS: Record<string, YapSortOption> = {
  'most-likes': 'most-engagement',
  'most-shouts': 'most-engagement',
};

const DEFAULT_SORT: YapSortOption = 'trending';

// Keys + window for the rolling Trending reset (see maybeResetSort).
const SORT_KEY       = 'yapTabActiveSort';
const RESET_AT_KEY   = 'yapTabTrendingResetAt';
// 30 minutes. Short on purpose: the sort is a browsing mode, not a preference,
// and leaving someone parked on Newest or Most Engagement for a whole day meant
// they stopped seeing what the feed was actually surfacing. Measured per device
// from its own last reset, so it drifts with usage rather than snapping on a
// shared wall-clock boundary.
const RESET_WINDOW_MS = 30 * 60 * 1000;
// How often the window is re-checked while the app sits open. Without this the
// check only ran on mount and on returning to the foreground, so a session left
// open simply never reset.
const RESET_CHECK_INTERVAL_MS = 60 * 1000;


interface YapDayContextType {
    currentYapDayStart: string; // ISO string of the global 12:00 AM EST
    timeUntilReset: string; // Formatted string HH:MM:SS
    refreshYapDay: () => void;
    activeSort: YapSortOption;
    setActiveSort: (sort: YapSortOption) => void;
    /**
     * Force the sort back to Trending if 24h have elapsed since the last reset.
     * Safe to call repeatedly — it no-ops inside the window. Called whenever
     * YapTab loads (mount + return to foreground), which is when the user would
     * actually see the change.
     */
    maybeResetSort: () => void;
}

const YapDayContext = createContext<YapDayContextType | undefined>(undefined);

export function YapDayProvider({ children }: { children: ReactNode }) {
    const [currentYapDayStart, setCurrentYapDayStart] = useState<string>('');
    const [timeUntilReset, setTimeUntilReset] = useState<string>('');
    // Helper to calculate the most recent 12:00 AM EST (UTC-5)
    // Note: EST is UTC-5. EDT is UTC-4. For simplicity and consistency, we might want to stick to a fixed offset 
    // or use a library like date-fns-tz if strict timezone handling including DST is required.
    // The requirement says "Fixed UTC offset (UTC-5/UTC-4)". 
    // Let's assume standard EST (UTC-5) for now to ensure a fixed anchor, unless specific DST handling is requested.
    // Actually, standard practice for "New York Time" includes DST. 
    // However, "Fixed UTC offset" implies we might simply want UTC-5 always to avoid jumps.
    // Let's use UTC-5 (Standard Time) as the fixed anchor as requested in "Reset Logic".
    const calculateYapDayStart = () => {
        const now = new Date();
        // Create a date object for the current time in UTC
        const nowUTC = now.getTime();

        // Target offset: UTC-5 (EST)
        const targetOffset = -5 * 60 * 60 * 1000;

        // We want to find the last 12:00 AM in UTC-5.
        // In UTC terms, 12:00 AM EST is 5:00 AM UTC.

        const dateInTargetZone = new Date(nowUTC + targetOffset);
        dateInTargetZone.setUTCHours(0, 0, 0, 0);

        // Convert back to real UTC timestamp
        const startOfDayUTC = dateInTargetZone.getTime() - targetOffset;

        return new Date(startOfDayUTC).toISOString();
    };

    const updateTimer = () => {
        const startStr = calculateYapDayStart();
        setCurrentYapDayStart(startStr);

        const now = new Date();
        const start = new Date(startStr);

        // The "end" of the current YapDay is start + 24 hours
        const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
        const diff = end.getTime() - now.getTime();

        if (diff <= 0) {
            // We just crossed the boundary! 
            // In a real app with strict intervals, we might trigger a hard reset here.
            // For now, calculating start again will auto-shift to the new day.
            const newStart = calculateYapDayStart();
            setCurrentYapDayStart(newStart);

            // Trigger global event
            window.dispatchEvent(new CustomEvent('GLOBAL_YAPDAY_RESET'));

            setTimeUntilReset("00:00:00");
        } else {
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            setTimeUntilReset(
                `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            );

            // Local notifications removed — GOLDEN_HOUR and YAPTAB_RESET are
            // delivered server-side via Cloud Function FCM topic pushes, which
            // are already received by every device subscribed to 'all_users'.
            // Scheduling them locally as well caused duplicate delivery and a
            // 1-hour timezone drift (server uses DST-aware America/New_York;
            // the old client code used a fixed UTC-5 offset).
        }
    };

    useEffect(() => {
        // Initial calc
        updateTimer();

        // Interval for timer
        const interval = setInterval(updateTimer, 1000);

        return () => clearInterval(interval);
    }, []);

    const refreshYapDay = () => {
        updateTimer();
    };

    // Filter/Sort Persistence
    const [activeSort, setActiveSortState] = useState<YapSortOption>(DEFAULT_SORT);

    const setActiveSort = (sort: YapSortOption) => {
        setActiveSortState(sort);
        SafeStorage.setItem(SORT_KEY, sort);
    };

    // ── Rolling 24h Trending reset ────────────────────────────────────────────
    // Trending is the default, and the user's choice only sticks for a day: 24h
    // after the last reset the sort snaps back to Trending on the next YapTab
    // load, whether or not they ever switched back themselves (e.g. after
    // tapping "New Yaps Dropped!", which parks them on Newest). The window is
    // per-user/per-device — measured from that device's last reset — rather than
    // a shared wall-clock boundary, so it drifts with each user's own usage.
    const maybeResetSort = React.useCallback(() => {
        const now = Date.now();
        const rawResetAt = Number(SafeStorage.getItem(RESET_AT_KEY) || 0);
        const resetAt = Number.isFinite(rawResetAt) ? rawResetAt : 0;

        // No stamp yet (first ever load), a corrupt stamp, or a stamp in the
        // future (clock moved backwards) → start a fresh window on Trending.
        const expired = !resetAt || resetAt > now || (now - resetAt) >= RESET_WINDOW_MS;
        if (!expired) return;

        SafeStorage.setItem(RESET_AT_KEY, String(now));
        setActiveSort(DEFAULT_SORT);
    }, []);

    useEffect(() => {
        const savedRaw = SafeStorage.getItem(SORT_KEY);
        const migrated = savedRaw ? SORT_MIGRATIONS[savedRaw] : undefined;

        if (migrated) {
            // Retired option (e.g. 'most-likes') → carry it to its replacement and
            // overwrite storage so the old value doesn't linger.
            setActiveSort(migrated);
        } else if (savedRaw && VALID_SORTS.includes(savedRaw as YapSortOption)) {
            setActiveSortState(savedRaw as YapSortOption);
        } else if (savedRaw) {
            // Unrecognised value (e.g. a previously-saved 'oldest') → default.
            setActiveSort(DEFAULT_SORT);
        }

        // Runs after the restore above so an expired window wins over the saved value.
        maybeResetSort();

        // Keep checking while the app stays open, so a long session still snaps
        // back to Trending on the half hour instead of only on the next launch.
        const timer = setInterval(maybeResetSort, RESET_CHECK_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [maybeResetSort]);

    return (
        <YapDayContext.Provider value={{
            currentYapDayStart,
            timeUntilReset,
            refreshYapDay,
            activeSort,
            setActiveSort,
            maybeResetSort
        }}>
            {children}
        </YapDayContext.Provider>
    );
}

export function useYapDay() {
    const context = useContext(YapDayContext);
    if (context === undefined) {
        throw new Error('useYapDay must be used within a YapDayProvider');
    }
    return context;
}
